import { useRef, useCallback } from 'react';

const LONG_PRESS_MS = 500;

interface UseLongPressOptions {
  onTap: (nodeIndex: number) => void;
  onLongPress: (nodeIndex: number) => void;
}

export function useLongPress({ onTap, onLongPress }: UseLongPressOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPressRef = useRef(false);
  const activeNodeRef = useRef<number | null>(null);
  const handlersCache = useRef<Map<number, ReturnType<typeof makeHandlers>>>(new Map());
  const depsRef = useRef({ onTap, onLongPress });

  // Update refs so cached handlers always call latest callbacks
  depsRef.current = { onTap, onLongPress };

  function makeHandlers(nodeIndex: number) {
    return {
      onPointerDown: (e: React.PointerEvent) => {
        e.preventDefault();
        isLongPressRef.current = false;
        activeNodeRef.current = nodeIndex;
        timerRef.current = setTimeout(() => {
          isLongPressRef.current = true;
          depsRef.current.onLongPress(nodeIndex);
        }, LONG_PRESS_MS);
      },
      onPointerUp: () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        if (!isLongPressRef.current && activeNodeRef.current !== null) {
          depsRef.current.onTap(activeNodeRef.current);
        }
        activeNodeRef.current = null;
      },
      onPointerLeave: () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        activeNodeRef.current = null;
      },
    };
  }

  const getHandlers = useCallback(
    (nodeIndex: number) => {
      let cached = handlersCache.current.get(nodeIndex);
      if (!cached) {
        cached = makeHandlers(nodeIndex);
        handlersCache.current.set(nodeIndex, cached);
      }
      return cached;
    },
    []
  );

  return { getHandlers };
}
